import styled from '@emotion/styled';
import {
  CircularProgress,
  Fade,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { GridPaginationModel } from '@mui/x-data-grid';
import { AxiosResponse } from 'axios';
import { concat } from 'lodash';
import debounce from 'lodash/debounce';
import { ManageCompetenciesEvent } from 'klayowebshared';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { track } from '../../common/track';
import { Button } from '../../components/Button';
import { Dialog } from '../../components/dialogs/Dialog';
import { Snackbar } from '../../components/Snackbar';
import StandardLibraryCheck from '../../components/StandardLibraryCheck';
import { StandardLibraryTable } from '../../components/table/StandardLibraryTable';
import { TextField } from '../../components/TextField';
import { useAppMutation } from '../../hooks/useAppMutation';
import { useAppQueryPost } from '../../hooks/useAppQuery';
import { ICompetencyImportResponse, ICompetencyListResponse } from '../../interfaces/Competency';
import { useLocale } from '../../providers/LocaleProvider';
import ChevronDownWhiteIcon from '../../resources/images/icons-chevron-down-white.svg';
import RemoveIcons from '../../resources/images/icons-remove.svg';
import SearchIcon from '../../resources/images/icons-search.svg';
import { KLAYO_COLORS } from '../../themes';
import ImportCompetenciesDialog from './ImportCompetenciesDialog';
import ImportCompeteniesErrorDialog from './ImportCompeteniesErrorDialog';
import LimitedOverlay from './LimitedOverlay';

export const ImportCompetenciesFromIndustryStandard = ({
  theme,
  onClose,
  organization,
  open,
}: any) => {
  const navigate = useNavigate();
  const { translate } = useLocale();
  const isTrial = !!organization?.isTrial || !organization?.enabledFeatures?.KlayoIntelligence;
  const [pagingModel, setPagingModel] = useState<GridPaginationModel>({ page: 0, pageSize: 5 });
  const [queryText, setQueryText] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');
  const [importMenuAnchorEl, setImportMenuAnchorEl] = useState<any>(null);
  const [importMenuOpen, setImportMenuOpen] = useState<boolean>(false);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [showImportCompetencies, setShowImportCompetencies] = useState(false);
  const [cacheRows, setCacheRows] = useState<ICompetencyImportResponse>({
    importedCompetencies: [],
    notImportedCompetencies: [],
    alreadyExistsCompetencies: [],
  });
  const [showMessage, setShowMessage] = useState(false);
  const [showImportCompetenciesError, setShowImportCompetenciesError] = useState(false);

  const { data: similarJob, isFetching } = useAppQueryPost<ICompetencyListResponse>(
    `/StandardLibrary/competencies`,
    {
      config: {
        params: {
          pageNumber: pagingModel.page + 1,
          pageSize: pagingModel.pageSize,
          searchText: (queryText || '').trim(),
          showExtra: true,
        },
      },
      placeholderData: undefined,
      enabled: organization?.enableStandardsLibrary,
    }
  );

  const importCompetenciesMutation = useAppMutation<
    AxiosResponse<ICompetencyImportResponse>,
    unknown,
    string[]
  >('post', '/StandardLibrary/competencies/import', {
    onSuccess: (data) => {
      setShowImportCompetencies(false);
      setCacheRows(data.data);
      setShowMessage(true);
    },
    onError: () => {},
  });

  const isLimitedAccess = useMemo(() => {
    if (!similarJob || !isTrial) return false;

    const competencies = similarJob.competencies?.data;
    return competencies.some((c) => c.isExtra);
  }, [similarJob, isTrial]);

  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setSelectedRowId(null);
    setPagingModel(newModel);
  };

  const onOpenDetail = () => {
    setImportMenuOpen(false);
    setShowImportCompetencies(true);
  };

  const debouncedSetQueryText = useCallback(
    debounce((value: string) => {
      if (value) track(ManageCompetenciesEvent.NewCompetencyIntelligenceSearch, { search: value });
      setQueryText(value);
      setPagingModel({ page: 0, pageSize: pagingModel.pageSize });
      setSelectedRowId(null);
    }, 300),
    [pagingModel, queryText]
  );

  const handleSearchChange = (e: any) => {
    setInputValue(e.target.value);
    debouncedSetQueryText(e.target.value);
  };

  const onCloseImportTask = () => {
    setShowImportCompetencies(false);
  };

  const onImportSingleCompetency = (competencyId: string) => {
    if (competencyId) {
      track(ManageCompetenciesEvent.NewCompetencyIntelligenceImport);
      importCompetenciesMutation.mutate([competencyId]);
    }
  };

  const handleActionMenu = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setImportMenuAnchorEl(null);
    setImportMenuOpen(true);
  };

  const setStateOfMessage = (state: boolean) => {
    setShowMessage(state);
  };

  const onImportCompetenciesError = () => {
    setShowImportCompetenciesError(true);
    setShowMessage(false);
  };

  const onCloseImportCompetenciesError = () => {
    setShowImportCompetenciesError(false);
  };

  const onImportCompetencies = () => {
    if (selectedRowId) {
      track(ManageCompetenciesEvent.NewCompetencyIntelligenceImport);
      importCompetenciesMutation.mutate([selectedRowId]);
    }
  };

  const onOpenImportAndEdit = () => {
    setImportMenuOpen(false);
    if (selectedRowId) {
      track(ManageCompetenciesEvent.NewCompetencyIntelligenceImport);
      importCompetenciesMutation.mutateAsync([selectedRowId]).then((res) => {
        if (res.data.importedCompetencies.length > 0) {
          onClose();
          navigate(
            `/settings/competencies/edit/${res.data.importedCompetencies[0].attributeDefinitionId}`
          );
        }
      });
    }
  };
  const similarJobs = useMemo(() => {
    if (!similarJob) return [];

    return similarJob?.competencies.data || [];
  }, [similarJob]);

  const extraCompetencies = useMemo(() => similarJobs.filter((c) => c.isExtra), [similarJobs]);

  const element = document.querySelector('#table-container-popup div.MuiDataGrid-main');

  const errorCompetencies = useMemo(
    () => concat(cacheRows.alreadyExistsCompetencies, cacheRows.notImportedCompetencies),
    [cacheRows.alreadyExistsCompetencies, cacheRows.notImportedCompetencies]
  );

  return (
    <StyledImportJobFromIndustryStandardDialog
      className='klayo__import-job-from-industry-standard-dialog'
      open={open}
      theme={theme}
      onClose={onClose}
      fullWidth
      maxWidth='sm'
    >
      <h1 className='truncate'>
        {translate('settingsViews.competencies.importCompetencies.title')}
      </h1>
      <StandardLibraryCheck>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='start'
          spacing={2}
          paddingBottom={2}
        >
          <div style={{ flex: 2 }}>
            <TextField
              dense={true}
              placeholder={translate(
                'settingsViews.competencies.importCompetencies.searchCompetency'
              )}
              autoComplete={false}
              leadingIcon={<SearchIcon />}
              fullWidth={true}
              onChange={handleSearchChange}
              value={inputValue}
            />
          </div>
        </Stack>

        <StyledStandardLibraryTable id='table-container-popup'>
          <StandardLibraryTable
            totalCount={similarJob?.competencies?.totalCount}
            rowHasAction={true}
            dense={true}
            rowHeight={44}
            paper={false}
            sortingMode='server'
            filterMode='server'
            theme={theme}
            showHeader={false}
            isLoading={isFetching}
            queryText={queryText}
            columns={[
              { type: StandardLibraryTable.columns.nameSuggestion },
              { type: StandardLibraryTable.columns.category, blur: true, align: 'right' },
            ]}
            paginationModel={pagingModel}
            onPaginationModelChange={handlePaginationModelChange as any}
            paginationMode='server'
            rows={(similarJobs ? similarJobs : null) as any}
            rowSelectionModel={selectedRowId ? [selectedRowId] : []}
            onJobSelect={(_event: any, row: any) => {
              if (row.isExtra) return;

              setSelectedRowId((prev) => (prev === row.id ? null : row.id));
            }}
            onActionMenu={handleActionMenu as any}
            sx={{
              '& .MuiDataGrid-selectedRowCount': {
                display: 'none',
              },
              '& .MuiTablePagination-root': {
                width: '100%',
              },
            }}
            showFooter={false}
            hideFirstLastBorder
          />
        </StyledStandardLibraryTable>
      </StandardLibraryCheck>
      <div className='button-container'>
        <div className='button-container-left'>
          <Button
            size='md'
            theme={theme}
            label={
              organization?.enableStandardsLibrary
                ? translate('common.cancel')
                : translate('common.close')
            }
            onClick={onClose as any}
          />
        </div>
        {organization?.enableStandardsLibrary && (
          <div className={`klaro_regulations-button-container`}>
            <Button
              size='md'
              theme={theme}
              variant='filled'
              label={
                <p style={{ fontSize: 14, padding: '0 .3rem' }}>
                  {translate('settingsViews.competencies.importCompetencies.import')}
                </p>
              }
              className='klaro_regulations-button-add-regulation'
              disabled={
                (!selectedRowId || importCompetenciesMutation.isPending) && !showImportCompetencies
              }
              onClick={onImportCompetencies as any}
              {...(importCompetenciesMutation.isPending &&
                !showImportCompetencies && { startIcon: <CircularProgress size={16} /> })}
            />
            <div className='klaro_regulations-vertical-line'></div>
            <div
              className='klaro_regulations-domain-category'
              data-disabled={!selectedRowId}
              style={{
                cursor: !selectedRowId ? 'not-allowed' : 'pointer',
                opacity: !selectedRowId ? 0.5 : 1,
                pointerEvents: !selectedRowId ? 'none' : 'auto',
              }}
            >
              <ChevronDownWhiteIcon
                onClick={(event: any) => {
                  if (!selectedRowId) return;
                  setImportMenuAnchorEl(event.currentTarget);
                  setImportMenuOpen(true);
                }}
                style={{
                  cursor: !selectedRowId ? 'not-allowed' : 'pointer',
                  opacity: !selectedRowId ? 0.5 : 1,
                }}
              />
            </div>
          </div>
        )}
      </div>

      <Menu
        open={importMenuOpen}
        anchorEl={importMenuAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={() => setImportMenuOpen(false)}
      >
        <MenuItem onClick={onOpenDetail}>
          <ListItemText>
            {translate('settingsViews.competencies.importCompetencies.viewDetail')}
          </ListItemText>
        </MenuItem>

        <MenuItem onClick={onOpenImportAndEdit}>
          <ListItemText>
            {translate('settingsViews.competencies.importCompetencies.importAndEdit')}
          </ListItemText>
        </MenuItem>
      </Menu>

      <Fade in={showImportCompetencies} timeout={300} unmountOnExit>
        <div>
          <ImportCompetenciesDialog
            theme={theme}
            competencyId={selectedRowId}
            onClose={onCloseImportTask}
            open={showImportCompetencies}
            onImport={onImportSingleCompetency}
            isImporting={importCompetenciesMutation.isPending}
          />
        </div>
      </Fade>

      <ImportCompeteniesErrorDialog
        theme={theme}
        open={showImportCompetenciesError}
        onClose={onCloseImportCompetenciesError}
        competencies={errorCompetencies}
      />

      <Fade in={showMessage} timeout={300} unmountOnExit>
        <div>
          <Snackbar
            open={true}
            autoHideDuration={10000}
            type={errorCompetencies.length > 0 ? 'error' : 'success'}
            onClose={() => setStateOfMessage(false)}
            message={
              <React.Fragment>
                <Typography hidden={errorCompetencies.length === 0}>
                  {errorCompetencies.length === 1
                    ? translate(
                        'settingsViews.competencies.importCompetencies.competencyNotImported',
                        { count: errorCompetencies.length }
                      )
                    : translate(
                        'settingsViews.competencies.importCompetencies.competenciesNotImported',
                        { count: errorCompetencies.length }
                      )}
                </Typography>
                <Typography hidden={!(errorCompetencies.length === 0)}>
                  {cacheRows.importedCompetencies.length === 1
                    ? translate(
                        'settingsViews.competencies.importCompetencies.competencyImported',
                        { count: cacheRows.importedCompetencies.length }
                      )
                    : translate(
                        'settingsViews.competencies.importCompetencies.competenciesImported',
                        { count: cacheRows.importedCompetencies.length }
                      )}
                </Typography>
              </React.Fragment>
            }
            action={
              <React.Fragment>
                <div
                  style={{
                    marginLeft: 'auto',
                    marginRight: '0.5rem',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                  }}
                  hidden={errorCompetencies.length === 0}
                  className='klayo-snackbar_text'
                  onClick={onImportCompetenciesError}
                >
                  {translate('settingsViews.competencies.importCompetencies.viewDetails')}
                </div>
                <BlackIconContainer
                  style={{
                    padding: '0',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                  }}
                  onClick={() => {
                    setStateOfMessage(false);
                  }}
                  className='klayo-snackbar_text '
                >
                  <RemoveIcons />
                </BlackIconContainer>
              </React.Fragment>
            }
          />
        </div>
      </Fade>

      {isLimitedAccess && (
        <LimitedOverlay
          element={element}
          height={extraCompetencies.length * 44}
          translate={translate}
        />
      )}
    </StyledImportJobFromIndustryStandardDialog>
  );
};

const StyledImportJobFromIndustryStandardDialog = styled(Dialog)`
  &.klayo__import-job-from-industry-standard-dialog {
    .klaro_regulations-button-container:has(button[disabled]) {
      opacity: 0.5;
      cursor: not-allowed;
      background-color: ${KLAYO_COLORS.GreyGR200} !important;
    }

    h1 {
      color: ${KLAYO_COLORS.GreyGR900};
      font-family: Inter;
      font-size: 24px;
      font-style: normal;
      font-weight: 600;
      line-height: 32px; /* 133.333% */
      letter-spacing: -0.5px;
      margin-top: 0px;
      margin-bottom: 32px;
    }
    .button-container {
      display: flex;
      justify-content: flex-end;
      padding-bottom: 30px;
      padding-top: 30px;
      gap: 10px;
    }
    h2 {
      color: ${KLAYO_COLORS.GreyGR900};
      font-family: Inter;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
      margin-top: 0px;
      margin-bottom: 24px;
    }

    .main-title {
      font-weight: 700;
    }

    .MuiDialogContent-root {
      padding: 40px 40px 0px 40px;
    }

    .MuiPaper-root {
      max-width: 800px;
    }

    .MuiDataGrid-cell {
      border-bottom: none !important;
    }

    .MuiDataGrid-footerContainer {
      border-top: none !important;
    }

    .MuiDataGrid-row {
      border-radius: 7px !important;

      &[aria-selected='true'] {
        background-color: ${KLAYO_COLORS.NeonBlueNB500} !important;

        & > *,
        & .MuiTypography-root {
          color: ${KLAYO_COLORS.WhiteWH900} !important;
        }
      }
    }

    .MuiDataGrid-selectedRowCount {
      visibility: hidden !important;
    }

    .MuiDataGrid-cell:has(p[data-style-id='flex-end']) {
      justify-content: flex-end !important;
    }
  }
`;

const StyledStandardLibraryTable = styled.div`
  .MuiDataGrid-columnHeader:focus-within {
    outline: none !important;
  }

  .Mui-disabled[aria-disabled='true'] {
    display: none;
  }
`;

const BlackIconContainer = styled.div`
  svg {
    path[fill='#1B3DF2'] {
      fill: #000;
    }
  }
`;
